import { supabase } from '../../utils/supabaseClient';

export async function getRateConfiguration(userId, productId, mode, customerId = null) {
    let query = supabase
        .from('rate_configurations')
        .select('*')
        .eq('user_id', userId)
        .eq('product_id', productId)
        .eq('mode', mode);

    if (customerId) {
        query = query.eq('customer_id', customerId);
    } else {
        // Explicitly check for null to distinguish from "any" if needed, 
        // but typically GLOBAL rates are where customer_id IS NULL
        query = query.is('customer_id', null);
    }

    const { data, error } = await query.maybeSingle();

    if (error && error.code !== 'PGRST116') throw error;
    return data || null;
}

export async function upsertRateConfiguration(userId, productId, mode, data, customerId = null) {
    const payload = {
        user_id: userId,
        product_id: productId,
        mode: mode,
        customer_id: customerId, // Add customer_id (can be null)
        slab_type: data.slabType,
        config: data.config,
        rates: data.rates,
        updated_at: new Date().toISOString()
    };

    const { data: result, error } = await supabase
        .from('rate_configurations')
        .upsert(payload, { onConflict: 'user_id, product_id, mode, customer_id' })
        .select()
        .single();

    if (error) throw error;
    return result;
}

export const findSector = (pincode, sectors) => {
    if (!pincode || !sectors || sectors.length === 0) return null;

    // Normalize pincode
    const pin = parseInt(pincode, 10);

    if (isNaN(pin)) {
        console.warn('Invalid pincode:', pincode);
        return null;
    }

    for (const sector of sectors) {
        // Handle property name mismatch (Profile saves 'pincodes', older code might expect 'pincode_ranges')
        const rawRanges = sector.pincodes || sector.pincode_ranges;

        if (!sector || !rawRanges) continue;

        // Robust Parsing using Regex Extraction instead of simple splitting
        // This handles "400000-403000, 404000-450000" and newline separated lists gracefully.

        // 1. Check Ranges
        const rangeRegex = /(\d+)\s*[-\u2013\u2014]\s*(\d+)/g;
        const rangeMatches = [...rawRanges.matchAll(rangeRegex)];

        for (const match of rangeMatches) {
            const start = parseInt(match[1], 10);
            const end = parseInt(match[2], 10);
            if (!isNaN(start) && !isNaN(end) && pin >= start && pin <= end) {
                console.log('  MATCH FOUND (Range check)!', start, end);
                return sector.id;
            }
        }

        // 2. Check Single Pincodes (that might not be in ranges)
        // Look for standalone numbers that weren't part of a range? 
        // Actually, just checking all numbers is fine if range didn't match.
        // We use \b to ensure we match whole numbers (mostly 6 digits for India)
        const singleRegex = /\b(\d{6})\b/g;
        const singleMatches = [...rawRanges.matchAll(singleRegex)];

        for (const match of singleMatches) {
            const exact = parseInt(match[1], 10);
            if (exact === pin) {
                console.log('  MATCH FOUND (Exact check)!', exact);
                return sector.id;
            }
        }


    }
    console.warn('No matching sector found for pincode:', pin);
    return null;
};

export const calculateRate = (config, rates, sectorId, weight) => {
    if (!config || !rates || !sectorId || !weight) return null;

    const sectorRates = rates[sectorId];
    if (!sectorRates) return null;

    // User requirement: Round up decimal weight to next whole number (e.g. 1.1 -> 2)
    const w = Math.ceil(parseFloat(weight));
    if (isNaN(w) || w <= 0) return null;

    // Check for both camelCase (from UI state) and snake_case (from DB row)
    const slabType = config.slabType || config.slab_type || 'slab2';
    const conf = config.config?.[slabType];

    if (!conf) return null;

    console.log('Calculation Debug:', {
        slabType,
        weight: w,
        conf,
        sectorRates
    });

    let amount = 0;

    // Get the Additional Weight Unit (step size), default to 1 if not set or invalid
    const addUnit = parseFloat(conf.add) > 0 ? parseFloat(conf.add) : 1;

    // Logic for Slab 2 (Base + Additional)
    // Uses keys: s2_base, s2_add (Fallback to legacy: base, add)
    if (slabType === 'slab2') {
        const baseWeight = parseFloat(conf.base || 0);

        const basePrice = parseFloat(sectorRates.s2_base !== undefined ? sectorRates.s2_base : (sectorRates.base || 0));
        const addPrice = parseFloat(sectorRates.s2_add !== undefined ? sectorRates.s2_add : (sectorRates.add || 0));

        if (w <= baseWeight) {
            amount = basePrice;
        } else {
            const excess = w - baseWeight;
            const units = Math.ceil(excess / addUnit);
            amount = basePrice + (units * addPrice);
        }
    }
    // Logic for Slab 3 (Base1 + Base2 + Additional)
    // Uses keys: s3_base1, s3_base2, s3_add (Fallback to legacy: base1, base2, add)
    else if (slabType === 'slab3') {
        const b1 = parseFloat(conf.base1 || 0);
        const b2 = parseFloat(conf.base2 || 0);

        const p1 = parseFloat(sectorRates.s3_base1 !== undefined ? sectorRates.s3_base1 : (sectorRates.base1 || 0));
        const p2 = parseFloat(sectorRates.s3_base2 !== undefined ? sectorRates.s3_base2 : (sectorRates.base2 || 0));
        const add = parseFloat(sectorRates.s3_add !== undefined ? sectorRates.s3_add : (sectorRates.add || 0));

        if (w <= b1) {
            amount = p1;
        } else if (w <= b2) {
            amount = p2;
        } else {
            const excess = w - b2;
            const units = Math.ceil(excess / addUnit);
            amount = p2 + (units * add);
        }
    }
    // Logic for Slab 4 (Base1 + Base2 + Base3 + Additional)
    // Uses keys: s4_base1, s4_base2, s4_base3, s4_add (Fallback to legacy: base1, base2, base3, add)
    else if (slabType === 'slab4') {
        const b1 = parseFloat(conf.base1 || 0);
        const b2 = parseFloat(conf.base2 || 0);
        const b3 = parseFloat(conf.base3 || 0);

        const p1 = parseFloat(sectorRates.s4_base1 !== undefined ? sectorRates.s4_base1 : (sectorRates.base1 || 0));
        const p2 = parseFloat(sectorRates.s4_base2 !== undefined ? sectorRates.s4_base2 : (sectorRates.base2 || 0));
        const p3 = parseFloat(sectorRates.s4_base3 !== undefined ? sectorRates.s4_base3 : (sectorRates.base3 || 0));
        const add = parseFloat(sectorRates.s4_add !== undefined ? sectorRates.s4_add : (sectorRates.add || 0));

        if (w <= b1) {
            amount = p1;
        } else if (w <= b2) {
            amount = p2;
        } else if (w <= b3) {
            amount = p3;
        } else {
            const excess = w - b3;
            const units = Math.ceil(excess / addUnit);
            amount = p3 + (units * add);
        }
    }

    // Round to 2 decimal places
    return Math.round(amount * 100) / 100;
};
